import JSZip from 'jszip';

const HEADER_FOOTER_PART = /^word\/(header|footer)(\d*)\.xml$/;
// Real headers and footers are a few KB, and Word writes at most six per
// section; anything past these limits is not worth expanding.
const MAX_PART_BYTES = 8 * 1024 * 1024;
const MAX_PARTS = 30;
const MAX_TOTAL_BYTES = 32 * 1024 * 1024;

type ZipEntryWithSize = JSZip.JSZipObject & { _data?: { uncompressedSize?: number } };

function partOrder(name: string): [number, number] {
  const [, kind, number] = HEADER_FOOTER_PART.exec(name) ?? [];
  return [kind === 'header' ? 0 : 1, Number(number || 0)];
}

function paragraphText(paragraph: Element): string {
  let output = '';
  paragraph.querySelectorAll('*').forEach((node) => {
    if (node.localName === 't') output += node.textContent ?? '';
    else if (node.localName === 'tab' || node.localName === 'br') output += ' ';
  });
  return output.split(/\s+/).filter(Boolean).join(' ');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

async function readHeaderFooterLines(arrayBuffer: ArrayBuffer): Promise<string[]> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const names = Object.keys(zip.files)
    .filter((name) => HEADER_FOOTER_PART.test(name))
    .sort((a, b) => {
      const [kindA, numA] = partOrder(a);
      const [kindB, numB] = partOrder(b);
      return kindA - kindB || numA - numB;
    })
    .slice(0, MAX_PARTS);

  const seen = new Set<string>();
  const lines: string[] = [];
  let totalBytes = 0;
  for (const name of names) {
    // `_data` is JSZip's per-entry metadata from the zip's central directory, so the
    // declared size is known before the part is expanded.
    const size = (zip.files[name] as ZipEntryWithSize)._data?.uncompressedSize ?? 0;
    if (size > MAX_PART_BYTES) continue;
    totalBytes += size;
    if (totalBytes > MAX_TOTAL_BYTES) break;
    const xml = await zip.files[name].async('string');
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) continue;
    const partLines = Array.from(doc.getElementsByTagNameNS('*', 'p'))
      .map(paragraphText)
      .filter(Boolean);
    // Word keeps first-page, even-page and default variants as separate parts,
    // usually with the same text.
    const key = `${partOrder(name)[0]}:${partLines.join('\n')}`;
    if (!partLines.length || seen.has(key)) continue;
    seen.add(key);
    lines.push(...partLines);
  }
  return lines;
}

/**
 * mammoth drops headers and footers, so text the chatbot can cite from them
 * has nowhere to be highlighted. Returns them as a block to append below the
 * converted body, or '' when there are none or the file can't be read.
 */
export async function readDocxHeaderFooterHtml(arrayBuffer: ArrayBuffer): Promise<string> {
  let lines: string[];
  try {
    lines = await readHeaderFooterLines(arrayBuffer);
  } catch {
    return '';
  }
  if (!lines.length) return '';
  const paragraphs = lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('');
  return (
    '<div class="docx-header-footer" style="margin-top:24px;padding-top:12px;' +
    `border-top:1px solid #d9d9d9;color:#595959;">${paragraphs}</div>`
  );
}
